/**
 * Public extension-slot surface for the orders module.
 *
 * Other modules (e.g. @pc/modules-contracts) may register UI that renders
 * inside the OrderDetailPage without the orders module ever importing
 * their internals. The dependency direction stays one-way: orders defines
 * the slot, dependents register into it.
 *
 * The slot registry is a process-global, so registrations made at module
 * load time (typically inside another module's `module.tsx`) are visible
 * to every OrderDetailPage that subsequently renders. Registrations are
 * de-duplicated by `id` so HMR doesn't pile up duplicates.
 *
 * Slot surface (kept intentionally small):
 *   - `order-detail.after-actions` -- renders below the "Next step" action
 *     card on OrderDetailPage.
 *   - `public-proposal.before-decision` -- renders above the approve /
 *     decline form on the public /p/:token page.
 */
import type { ComponentType } from 'react';

export interface OrderDetailSlotContext {
  orderId: string;
  orgId: string;
  storeId: string;
  status: string;
}

/**
 * Slot context for the FulfillmentCard's "ship" branch. The shipping
 * module registers into this slot at load time so the orders module
 * doesn't need to import @pc/modules-shipping (one-way dependency).
 */
export interface FulfillmentShipSlotContext {
  orderId: string;
  orgId: string;
  storeId: string;
  /** Snapshot of orders.orders.shipping_address_snapshot (jsonb). */
  shipToSnapshot: Record<string, unknown> | null;
}

export interface PublicProposalSlotAcceptance {
  proposalContractId: string;
  acceptorName: string;
  signatureText?: string;
  consentElectronicSignature: boolean;
}

export interface PublicProposalSlotContext {
  token: string;
  orderNumber: string;
  status: string;
  /**
   * Called by the slot when the user updates acceptances (e.g. types
   * their name or toggles the consent checkbox). The host page collects
   * these and forwards them with the approval RPC.
   */
  onAcceptancesChange?: (acceptances: PublicProposalSlotAcceptance[]) => void;
  /**
   * Called by the slot whenever the validity changes (all required
   * contracts signed + consented). The host disables the approve button
   * until valid is true.
   */
  onValidityChange?: (valid: boolean) => void;
  /** Called by the slot when it wants the host to refetch the proposal. */
  onChange?: () => Promise<void> | void;
}

export type OrderDetailSlotComponent = ComponentType<OrderDetailSlotContext>;
export type FulfillmentShipSlotComponent = ComponentType<FulfillmentShipSlotContext>;
export type PublicProposalSlotComponent = ComponentType<PublicProposalSlotContext>;

interface SlotRegistration<P> {
  id: string;
  order: number;
  render: ComponentType<P>;
}

const orderDetailSlots: SlotRegistration<OrderDetailSlotContext>[] = [];
const fulfillmentShipSlots: SlotRegistration<FulfillmentShipSlotContext>[] = [];
const publicProposalSlots: SlotRegistration<PublicProposalSlotContext>[] = [];

export interface OrderDetailSlotRegistration {
  id: string;
  order?: number;
  render: OrderDetailSlotComponent;
}

export function registerOrderDetailSlot(reg: OrderDetailSlotRegistration): () => void {
  const entry = { id: reg.id, order: reg.order ?? 100, render: reg.render };
  const i = orderDetailSlots.findIndex((s) => s.id === reg.id);
  if (i >= 0) orderDetailSlots[i] = entry;
  else orderDetailSlots.push(entry);
  return () => {
    const j = orderDetailSlots.findIndex((s) => s.id === reg.id);
    if (j >= 0) orderDetailSlots.splice(j, 1);
  };
}

export function getOrderDetailSlots(): ReadonlyArray<SlotRegistration<OrderDetailSlotContext>> {
  return [...orderDetailSlots].sort((a, b) => a.order - b.order);
}

export interface FulfillmentShipSlotRegistration {
  id: string;
  order?: number;
  render: FulfillmentShipSlotComponent;
}

export function registerFulfillmentShipSlot(
  reg: FulfillmentShipSlotRegistration,
): () => void {
  const entry = { id: reg.id, order: reg.order ?? 100, render: reg.render };
  const i = fulfillmentShipSlots.findIndex((s) => s.id === reg.id);
  if (i >= 0) fulfillmentShipSlots[i] = entry;
  else fulfillmentShipSlots.push(entry);
  return () => {
    const j = fulfillmentShipSlots.findIndex((s) => s.id === reg.id);
    if (j >= 0) fulfillmentShipSlots.splice(j, 1);
  };
}

export function getFulfillmentShipSlots(): ReadonlyArray<
  SlotRegistration<FulfillmentShipSlotContext>
> {
  return [...fulfillmentShipSlots].sort((a, b) => a.order - b.order);
}

export interface PublicProposalSlotRegistration {
  id: string;
  order?: number;
  render: PublicProposalSlotComponent;
}

export function registerPublicProposalSlot(reg: PublicProposalSlotRegistration): () => void {
  const entry = { id: reg.id, order: reg.order ?? 100, render: reg.render };
  const i = publicProposalSlots.findIndex((s) => s.id === reg.id);
  if (i >= 0) publicProposalSlots[i] = entry;
  else publicProposalSlots.push(entry);
  return () => {
    const j = publicProposalSlots.findIndex((s) => s.id === reg.id);
    if (j >= 0) publicProposalSlots.splice(j, 1);
  };
}

export function getPublicProposalSlots(): ReadonlyArray<
  SlotRegistration<PublicProposalSlotContext>
> {
  return [...publicProposalSlots].sort((a, b) => a.order - b.order);
}
